import { getInjectedJsonOrFail } from '@/services/get-injected-json';
import { keyBy } from '@/utilities/array';
import { GRAPHICS_CATEGORY_ID } from '@/utilities/consts';
import { showTemplateDifficultyLevelFilter } from '@/utilities/browse-helper';

const injectedBrowseCategoryFilters = getInjectedJsonOrFail('browse_category_filters');
const {
  categories, resolutions, fps, orientations, art_styles, themes: themesBase,
} = injectedBrowseCategoryFilters;

export const DEFAULT_CATEGORY_SLUG = 'after-effects-templates';
export const CATEGORY_RESOLUTION_DEFAULT_SLUG = 'all-resolutions';
export const CATEGORY_ORIENTATION_DEFAULT_SLUG = 'all-orientations';
export const CATEGORY_FPS_DEFAULT_SLUG = 'all-fps';
export const CATEGORY_VERSION_DEFAULT_SLUG = 'all-versions';
export const CATEGORY_EDITORIAL_DEFAULT_SLUG = 'all-editorials';
export const CATEGORY_PRO_AFFILIATION_DEFAULT_SLUG = 0;
export const CATEGORY_RIGHTS_MANAGERS_DEFAULT_SLUG = 0;
export const CATEGORY_PEOPLE_DEFAULT_SLUG = 0;

export const editorials = [
  {
    slug: CATEGORY_EDITORIAL_DEFAULT_SLUG,
    name: 'All Usage Rights',
  },
  {
    slug: 'exclude',
    name: 'Commercial Use',
  },
  {
    slug: 'only',
    name: 'Editorial Use',
  },
];

export const proAffiliations = [
  {
    slug: CATEGORY_PRO_AFFILIATION_DEFAULT_SLUG,
    name: 'All Usage Rights',
  },
  {
    slug: -1,
    name: 'Exclude P.R.O',
  },
  {
    slug: 1,
    name: 'Only P.R.O',
  },
];

export const rightsManagers = [
  {
    slug: CATEGORY_RIGHTS_MANAGERS_DEFAULT_SLUG,
    name: 'All Content ID Statuses',
  },
  {
    slug: -1,
    name: 'Exclude Content ID',
  },
  {
    slug: 1,
    name: 'Only Content ID',
  },
];

export const people = [
  {
    slug: CATEGORY_PEOPLE_DEFAULT_SLUG,
    name: 'With & Without People',
  },
  {
    slug: 1,
    name: 'With People',
  },
  {
    slug: -1,
    name: 'Without People',
  },
];

const fileTypesBase = [
  {
    slug: 'eps',
    name: 'EPS',
  },
  {
    slug: 'svg',
    name: 'SVG',
  },
  {
    slug: 'png',
    name: 'PNG',
  },
  {
    slug: 'jpeg',
    name: 'JPEG',
  },
  {
    slug: 'jpg',
    name: 'JPG',
  },
  {
    slug: 'ai',
    name: 'AI',
  },
  {
    slug: 'psd',
    name: 'PSD',
  },
  {
    slug: 'indd',
    name: 'INDD',
  },
  {
    slug: 'tdf',
    name: 'TDF',
  },
  {
    slug: 'tiff',
    name: 'TIFF',
  },
  {
    slug: 'dxf',
    name: 'DXF',
  },
  {
    slug: 'dwg',
    name: 'DWG',
  },
  {
    slug: 'fig',
    name: 'FIG',
  },
];

const applicationsSupportedBase = [
  {
    slug: 'adobe-photoshop',
    name: 'Adobe Photoshop',
  },
  {
    slug: 'adobe-illustrator',
    name: 'Adobe Illustrator',
  },
  {
    slug: 'in-design',
    name: 'InDesign',
  },
];

export const CATEGORY_BY_ID = keyBy(categories, 'id');

orientations.unshift({
  slug: CATEGORY_ORIENTATION_DEFAULT_SLUG,
  name: 'All Orientations',
});

fps.unshift({
  slug: CATEGORY_FPS_DEFAULT_SLUG,
  name: 'All FPSs',
});

resolutions.unshift({
  slug: CATEGORY_RESOLUTION_DEFAULT_SLUG,
  name: 'All Resolutions',
});
export const FPS_BY_SLUG = keyBy(fps, 'slug');
export const RESOLUTION_BY_SLUG = keyBy(resolutions, 'slug');
export const ORIENTATION_BY_SLUG = keyBy(orientations, 'slug');
export const PEOPLE_BY_SLUG = keyBy(people, 'slug');
export const CATEGORY_VERSION_BY_SLUG = {};
export const CATEGORY_EDITORIAL_BY_SLUG = {};

categories.forEach((item) => {
  if (item.has_versions) {
    const allVersions = {
      slug: CATEGORY_VERSION_DEFAULT_SLUG,
      name: 'All Versions',
    };
    const versions = [allVersions].concat(item.versions);
    CATEGORY_VERSION_BY_SLUG[item.slug] = keyBy(versions, 'slug');
  }
  if (item.can_be_used_for_editorial_use) {
    CATEGORY_EDITORIAL_BY_SLUG[item.slug] = keyBy(editorials, 'slug');
  }
});

const generateFilter = (filterItems) => {
  const items = {};
  filterItems.forEach((filterItem) => {
    items[filterItem.slug] = {
      name: filterItem.name,
      slug: filterItem.slug,
      selected: false,
    };
  });
  return items;
};

export function freshCategoryFilters(categorySlug) {
  let editorial = {};
  let fileTypes = {};
  let applicationsSupported = {};
  let artStyles = {};
  let themes = {};
  let graphicsFilters = {};
  let templatesFilters = {};

  const category = categories.find(item => item.slug === categorySlug);
  const subCategories = generateFilter(category.sub_categories);
  const isGraphicsCategory = category.id === GRAPHICS_CATEGORY_ID;

  const addTemplateDifficultyLevelFilter = showTemplateDifficultyLevelFilter(category.id);

  if (isGraphicsCategory) {
    fileTypes = generateFilter(fileTypesBase);
    applicationsSupported = generateFilter(applicationsSupportedBase);
    artStyles = generateFilter(art_styles);
    themes = generateFilter(themesBase);

    graphicsFilters = {
      isVector: false,
      isIsolated: false,
      artStyles,
      themes,
      fileTypes,
      applicationsSupported,
      people: PEOPLE_BY_SLUG[CATEGORY_PEOPLE_DEFAULT_SLUG],
    };
  }

  if (addTemplateDifficultyLevelFilter) {
    templatesFilters = {
      templateDifficultyLevel: null
    };
  }

  if (category.can_be_used_for_editorial_use) {
    editorial = CATEGORY_EDITORIAL_BY_SLUG[category.slug][CATEGORY_EDITORIAL_DEFAULT_SLUG];
  }

  return {
    id: category.id,
    slug: category.slug,
    name: category.name,
    shortName: category.short_name,
    subCategories,
    hasResolutions: category.has_resolutions,
    hasFps: category.has_fpss,
    resolution: [],
    fps: FPS_BY_SLUG[CATEGORY_FPS_DEFAULT_SLUG],
    orientation: [],
    hasVersions: category.has_versions,
    version: [],
    hasBpms: category.has_bpms,
    proAffiliated: 0,
    hasProAffiliation: category.has_bpms,
    bpms: [],
    durations: [],
    hasRightsManagers: category.has_rights_managers,
    rightsManager: 0,
    editorial,
    hasEditorials: category.can_be_used_for_editorial_use,
    ...(isGraphicsCategory && { ...graphicsFilters }),
    ...(addTemplateDifficultyLevelFilter &&  {...templatesFilters}),
  };
}

let legacyCategorySlugs = {};

export function setLegacyCategorySlugs(map) {
  legacyCategorySlugs = map;
}

export function normalizeCategorySlug(slug) {
  if (legacyCategorySlugs[slug]) {
    return legacyCategorySlugs[slug];
  }
  return slug;
}

let legacySubCategorySlugs = {};

export function setLegacySubCategorySlugs(map) {
  legacySubCategorySlugs = map;
}

export function normalizeSubCategorySlug(categorySlug, slug) {
  if (
    legacySubCategorySlugs[categorySlug]
        && legacySubCategorySlugs[categorySlug][slug]
  ) {
    return legacySubCategorySlugs[categorySlug][slug];
  }
  return slug;
}
